import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, TrendingDown, Wallet } from "lucide-react";

export interface IncomeExpenseCategory {
  id: string;
  label: string;
  type: "einnahmen" | "ausgaben";
  items: { id: string; label: string }[];
}

interface IncomeExpenseTableProps {
  categories?: IncomeExpenseCategory[];
  value: Record<string, Record<string, number>>;
  onChange: (value: Record<string, Record<string, number>>) => void;
  showGP2?: boolean;
  className?: string;
}

const DEFAULT_CATEGORIES: IncomeExpenseCategory[] = [
  {
    id: "einkommen",
    label: "Einkommen",
    type: "einnahmen",
    items: [
      { id: "nettoeinkommen", label: "Nettoeinkommen" },
      { id: "sonderzahlungen", label: "Sonderzahlungen (1/12)" },
      { id: "mieteinnahmen", label: "Mieteinnahmen" },
      { id: "sonstige", label: "Sonstige Einnahmen" },
    ],
  },
  {
    id: "wohnen",
    label: "Wohnen",
    type: "ausgaben",
    items: [
      { id: "miete", label: "Miete / Kredit" },
      { id: "betriebskosten", label: "Betriebskosten" },
      { id: "strom", label: "Strom / Gas" },
      { id: "versicherungen_wohnen", label: "Versicherungen" },
    ],
  },
  {
    id: "mobilitaet",
    label: "Mobilität",
    type: "ausgaben",
    items: [
      { id: "kfz_kredit", label: "KFZ Kredit / Leasing" },
      { id: "kfz_versicherung", label: "KFZ Versicherung" },
      { id: "treibstoff", label: "Treibstoff" },
      { id: "oeffentlich", label: "Öffentliche Verkehrsmittel" },
    ],
  },
  {
    id: "leben",
    label: "Lebenshaltung",
    type: "ausgaben",
    items: [
      { id: "lebensmittel", label: "Lebensmittel" },
      { id: "kleidung", label: "Kleidung" },
      { id: "freizeit", label: "Freizeit / Urlaub" },
      { id: "kommunikation", label: "Telefon / Internet" },
    ],
  },
  {
    id: "vorsorge",
    label: "Vorsorge",
    type: "ausgaben",
    items: [
      { id: "sparen", label: "Sparen / Investieren" },
      { id: "versicherungen", label: "Versicherungen" },
      { id: "altersvorsorge", label: "Altersvorsorge" },
    ],
  },
];

export function IncomeExpenseTable({
  categories = DEFAULT_CATEGORIES,
  value = {},
  onChange,
  showGP2 = true,
  className,
}: IncomeExpenseTableProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-AT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const handleChange = (
    categoryId: string,
    itemId: string,
    gp: "gp1" | "gp2",
    newValue: number
  ) => {
    const key = `${categoryId}_${itemId}`;
    onChange({
      ...value,
      [key]: {
        ...(value[key] || {}),
        [gp]: newValue,
      },
    });
  };

  const getValue = (categoryId: string, itemId: string, gp: "gp1" | "gp2"): number => {
    const key = `${categoryId}_${itemId}`;
    return value[key]?.[gp] || 0;
  };

  const totals = useMemo(() => {
    const result = { einnahmen: { gp1: 0, gp2: 0 }, ausgaben: { gp1: 0, gp2: 0 } };
    
    categories.forEach((cat) => {
      cat.items.forEach((item) => {
        const key = `${cat.id}_${item.id}`;
        result[cat.type].gp1 += value[key]?.gp1 || 0;
        result[cat.type].gp2 += value[key]?.gp2 || 0;
      });
    });
    
    return result;
  }, [value, categories]);

  const freeCapital = {
    gp1: totals.einnahmen.gp1 - totals.ausgaben.gp1,
    gp2: totals.einnahmen.gp2 - totals.ausgaben.gp2,
    total: totals.einnahmen.gp1 + totals.einnahmen.gp2 - totals.ausgaben.gp1 - totals.ausgaben.gp2,
  };

  const einnahmenCategories = categories.filter((c) => c.type === "einnahmen");
  const ausgabenCategories = categories.filter((c) => c.type === "ausgaben");

  const renderCategory = (category: IncomeExpenseCategory) => (
    <div key={category.id} className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">{category.label}</h4>
      
      {category.items.map((item) => (
        <div key={item.id} className="grid gap-2" style={{ gridTemplateColumns: showGP2 ? "1fr 120px 120px" : "1fr 120px" }}>
          <Label className="text-sm self-center">{item.label}</Label>
          
          <div className="relative">
            <Input
              type="number"
              value={getValue(category.id, item.id, "gp1") || ""}
              onChange={(e) =>
                handleChange(category.id, item.id, "gp1", parseFloat(e.target.value) || 0)
              }
              className="pr-6 h-8 text-right text-sm"
              placeholder="0"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
              €
            </span>
          </div>
          
          {showGP2 && (
            <div className="relative">
              <Input
                type="number"
                value={getValue(category.id, item.id, "gp2") || ""}
                onChange={(e) =>
                  handleChange(category.id, item.id, "gp2", parseFloat(e.target.value) || 0)
                }
                className="pr-6 h-8 text-right text-sm"
                placeholder="0"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                €
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderSummaryRow = (
    label: string,
    gp1: number,
    gp2: number,
    icon: React.ReactNode,
    colorClass: string
  ) => (
    <div
      className="grid gap-2 py-2"
      style={{ gridTemplateColumns: showGP2 ? "1fr 120px 120px" : "1fr 120px" }}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium">{label}</span>
      </div>
      <span className={cn("text-right font-semibold", colorClass)}>
        {formatCurrency(gp1)}
      </span>
      {showGP2 && (
        <span className={cn("text-right font-semibold", colorClass)}>
          {formatCurrency(gp2)}
        </span>
      )}
    </div>
  );

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div
        className="grid gap-2 pb-2 border-b"
        style={{ gridTemplateColumns: showGP2 ? "1fr 120px 120px" : "1fr 120px" }}
      >
        <div />
        <span className="text-sm font-medium text-center">GP 1</span>
        {showGP2 && <span className="text-sm font-medium text-center">GP 2</span>}
      </div>

      {/* Einnahmen */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg space-y-4">
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
          <TrendingUp className="h-5 w-5" />
          <h3 className="font-semibold">Einnahmen</h3>
        </div>
        {einnahmenCategories.map(renderCategory)}
        <div className="pt-2 border-t border-emerald-200 dark:border-emerald-800">
          {renderSummaryRow(
            "Summe Einnahmen",
            totals.einnahmen.gp1,
            totals.einnahmen.gp2,
            <TrendingUp className="h-4 w-4 text-emerald-600" />,
            "text-emerald-600"
          )}
        </div>
      </div>

      {/* Ausgaben */}
      <div className="p-4 bg-rose-50 dark:bg-rose-950/20 rounded-lg space-y-4">
        <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
          <TrendingDown className="h-5 w-5" />
          <h3 className="font-semibold">Ausgaben</h3>
        </div>
        {ausgabenCategories.map(renderCategory)}
        <div className="pt-2 border-t border-rose-200 dark:border-rose-800">
          {renderSummaryRow(
            "Summe Ausgaben",
            totals.ausgaben.gp1,
            totals.ausgaben.gp2,
            <TrendingDown className="h-4 w-4 text-rose-600" />,
            "text-rose-600"
          )}
        </div>
      </div>

      {/* Freies Kapital */}
      <div className="p-4 bg-primary/10 rounded-lg">
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: showGP2 ? "1fr 120px 120px" : "1fr 120px" }}
        >
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            <span className="font-semibold text-lg">Freies Kapital</span>
          </div>
          <span
            className={cn(
              "text-right font-bold text-lg",
              freeCapital.gp1 >= 0 ? "text-emerald-600" : "text-destructive"
            )}
          >
            {formatCurrency(freeCapital.gp1)}
          </span>
          {showGP2 && (
            <span
              className={cn(
                "text-right font-bold text-lg",
                freeCapital.gp2 >= 0 ? "text-emerald-600" : "text-destructive"
              )}
            >
              {formatCurrency(freeCapital.gp2)}
            </span>
          )}
        </div>
        
        {showGP2 && (
          <div className="mt-3 pt-3 border-t border-primary/20 flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Gesamt (GP1 + GP2)</span>
            <span
              className={cn(
                "font-bold text-xl",
                freeCapital.total >= 0 ? "text-emerald-600" : "text-destructive"
              )}
            >
              {formatCurrency(freeCapital.total)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
